export type OpenSignupEvent = {
    origin: 'header' | 'welcomeScreen' | 'loginDialog' | 'guestWelcome' | 'guestProfileDropdown' | 'puzzleLimit'
}

export type OpenLoginEvent = {
    origin: 'header' | 'welcomeScreen' | 'signupDialog' | 'guestWelcome' | 'guestProfileDropdown' | 'puzzleLimit'
}

/** Event fired when user successfully completes signup process */
export type CompleteSignupEvent = {
    country: string
    authType: 'email/user_name' | 'google' | 'apple' | 'facebook' | 'guest'
    userId?: string
    userName?: string
}

export type CompleteLoginEvent = {
    authType: 'email/user_name' | 'google' | 'apple' | 'facebook'
    userId: string
    userName: string
}

export type LogoutEvent = {}

export type CustomChallengeEvent = {
    minutes: number // this or challengeString?
    increment: number
    rated: boolean
    color: 'white' | 'black' | 'noColor'
    ratingFrom: number
    ratingTo: number
}

export type BotChallengeEvent = {
    minutes: number
    increment: number
    difficulty: number
    color: 'white' | 'black' | 'noColor'
    startingFEN: string
    botName: string
    botId: string
    botID?: string // GTM expects botID (different from botId)
}

export type EnterAutoPoolEvent = {
    gameType: 'bullet' | 'blitz' | 'rapid' | 'classic'
    minutes: number
    increment: number
    challengeID: string
    challengeString?: string // GTM expects this parameter
    challengeType?: string // GTM expects this parameter
}

export type ExitAutoPoolEvent = {
    gameType: 'bullet' | 'blitz' | 'rapid' | 'classic'
    minutes: number
    increment: number
    challengeID: string
    challengeString?: string // GTM expects this parameter
    challengeType?: string // GTM expects this parameter
}

/** Event fired when a chess game begins - includes comprehensive game and player data */
export type GameStartedEvent = {
    matchKind: 'human_vs_human' | 'human_vs_ai'
    minutes: number
    increment: number
    rated: boolean
    timeInQueue: number
    player1ID: string
    player2ID: string
    player1Color: 'white' | 'black'
    player2Color: 'white' | 'black'
    matchID: string
    challengeString?: string // GTM expects this parameter
    matchSource?: string // GTM expects this parameter
    difficulty?: number // GTM expects this parameter for AI games
}

export type ResignEvent = {
    minutes: number
    increment: number
    color: 'white' | 'black'
    rated: boolean
    opponentID: string
    matchID: string
}

export type GameOfferSentEvent = {
    type: 'draw' | 'abort'
    minutes: number
    increment: number
    color: 'white' | 'black' | 'noColor'
    rated: boolean
    opponentID: string
    matchID: string
}

export type GameOfferResultEvent = {
    type: 'draw' | 'abort'
    result: 'accepted' | 'rejected'
    minutes: number
    increment: number
    color: 'white' | 'black' | 'noColor'
    rated: boolean
    opponentID: string
    matchID: string
}

export type OfferRematchEvent = {
    minutes: number
    increment: number
    color: 'white' | 'black' | 'noColor'
    rated: boolean
    opponentID: string
    parentMatchID: string
}

export type OfferRematchResultEvent = {
    result: 'accepted' | 'rejected'
    parentMatchID: string
}

export type GameFinishedEvent = {
    matchKind: 'human_vs_human' | 'human_vs_ai'
    minutes: number
    increment: number
    method: string
    reason: string
    rated: boolean
    player1ID: string
    player2ID: string
    player1Color: 'white' | 'black'
    player2Color: 'white' | 'black'
    whiteClockLeft: number
    blackClockLeft: number
    matchID: string
    timeInGame: number
    rating: number
}

export type ClickVideoEvent = {
    videoUrl: string
}

export type CompletedPuzzleEvent = {
    origin: 'puzzleQuest' | 'dailyPuzzle' | 'practicePuzzle'
    result: 'solution_showed' | 'passed' | 'skipped' | 'failed'
    attempts: number
    usedHints: number
    puzzleSource: number
    userPuzzleRating: number
    puzzleId: string
    puzzleRating: number
    datePlayed?: string // GTM expects this parameter
}

export type RewardEvent = {
    type: 'frame' | 'crown'
    tier: number
    amount: number
    expirationDate: string
}

export type DirectChallengeEvent = {
    minutes: number
    increment: number
    color: 'white' | 'black' | 'noColor'
    rated: boolean
    userID: string
    opponentID: string
}

export type EmailActivatedEvent = {}

export type LoginPromptShownEvent = {}

export type GuestContinuedEvent = {}

export type LoginDismissedEvent = {}

/** Event fired when user confirms their email address */
export type ConfirmEmailEvent = {}

/** Event fired when user requests email confirmation resend */
export type RequestResendEvent = {}

export type CloseGuestWelcomeEvent = {}

export type CreateGuestAccountEvent = {}

export type SignupReachSecondPageEvent = {
    subscribeToNewsletter: boolean
}

/** Event fired when user moves to the next puzzle (completion tracking) */
export type NextPuzzleEvent = {
    puzzleId: string
    datePlayed: string
    puzzleRating: number
    userRating: number
    result: string
    attempts: number
    usedHints: number
}

export type MissionCompletedEvent = {
    missionType: string
    missionName: string
    missionId: number
}

export type MissionClaimedEvent = {
    missionType: string
    missionName: string
    missionId: number
}

/** GTM expects these as separate events from the generic gameOfferSent */
export type OfferAbortEvent = {
    minutes: number
    increment: number
    color: 'white' | 'black' | 'noColor'
    rated: boolean
    opponentID: string
    matchID: string
}

export type OfferDrawEvent = {
    minutes: number
    increment: number
    color: 'white' | 'black' | 'noColor'
    rated: boolean
    opponentID: string
    matchID: string
}

/** GTM expects this as a separate event from offerRematchResult */
export type AcceptRematchEvent = {
    parentMatchID: string
}

/** GTM expects this as an alias for botChallenge */
export type ComputerChallengeEvent = {
    minutes: number
    increment: number
    difficulty: number
    color: 'white' | 'black' | 'noColor'
    gameType: 'bullet' | 'blitz' | 'rapid' | 'classic'
}

/** GTM expects this as a standalone event */
export type TimeInQueueEvent = {
    timeInQueue: number
    rated: boolean
    challengeString: string
    challengeType: string
    matchedFromQueue: boolean
}

export type FtuePuzzleStartedEvent = {
    puzzleTag: string
    puzzleId: string
}

export type FtuePuzzleCompletedEvent = {
    puzzleTag: string
    puzzleId: string
}

export type PopupEvents = {
    popupName: string
    popupId: number
}

export type PopupShownEvent = {
    popupName: string
    popupId: number
}

export type PopupClosedEvent = {
    popupName: string
    popupId: number
}

export type CTASelectedEvent = {
    popupName: string
    popupId: number
}

export type DailyBonusClickedEvent = {
    pageUrl: string
}

export type DailyBonusCollectedEvent = {
    pageUrl: string
    rewardType: 'hints'
    rewardAmount: number
}

export type PowerUpUsedEvent = {
    type: 'hints'
    oldArgument: number
    newArgument: number
    argumentChange: number
    feature: 'puzzleQuest' | 'dailyPuzzle' | 'practicePuzzle'
    puzzleId: string
}

export type FtueEndDialogueEvent = {
    buttonPressed: 'close' | 'keepPlaying' | 'exploreMore'
}

export type FeedbackPopupShownEvent = {
    puzzleId: string
    groupId: number
    percentileRaw: number
    feedbackScore: number
    streakCount: number
    usedShowSolution: boolean
    dynamicText: string
    feature: 'dailyPuzzle' | 'puzzleQuest'
}

export type FeedbackPopupClosedEvent = {
    puzzleId: string
    feature: 'dailyPuzzle' | 'puzzleQuest'
}

export type MenuButtonClickedEvent = {
    buttonPressed: 'playNow' | 'playBots' | 'puzzles' | 'dailyPuzzle' | 'puzzleQuest'
}

export type StartedPuzzleEvent = {
    puzzleId: string
    puzzleRating: number
    puzzleTags: string
    origin: 'puzzleQuest' | 'dailyPuzzle' | 'practicePuzzle'
    userPuzzleRating: number
}

export type GameButtonPressedEvent = {
    eventLocation: 'gameSummaryDialogue' | 'postGameSummaryScreen' | 'watchSummaryDialogue'
    buttonIndex: number
}

export type FlipBoardPressedEvent = {
    mode: 'pvp' | 'pve' | 'analysis'
}

export type ShareClickedEvent = {
    puzzleId: string
    groupId: number
    linkId: string
    feature: 'dailyPuzzle' | 'puzzleQuest'
}

export type ShareLinkCopiedEvent = {
    puzzleId: string
    groupId: number
    linkId: string
    feature: 'dailyPuzzle' | 'puzzleQuest'
}

export type FeedbackSharedEvent = {
    puzzleId: string
    groupId: number
    linkId: string
    feature: 'dailyPuzzle' | 'puzzleQuest'
}

export type PvpEmoteSentEvent = {
    emoteId: string // the CS game_messages id (sent to GS as messageId)
    matchID: string
    matchKind: string
    rated: boolean
    minutes: number
    increment: number
    opponentID: string
    phase: 'inGame' | 'endOfGame'
}

export type PvpEmoteMenuOpenedEvent = {
    matchID: string
    phase: 'inGame' | 'endOfGame'
}

export type AnalyticsEventMap = {
    botChallenge: BotChallengeEvent
    completeLogin: CompleteLoginEvent
    completeSignup: CompleteSignupEvent
    openSignup: OpenSignupEvent
    clickVideo: ClickVideoEvent
    logout: LogoutEvent
    gameOfferSent: GameOfferSentEvent
    gameOfferResult: GameOfferResultEvent
    openLogin: OpenLoginEvent
    exitAutoPool: ExitAutoPoolEvent
    customChallenge: CustomChallengeEvent
    offerRematch: OfferRematchEvent
    resign: ResignEvent
    completedPuzzle: CompletedPuzzleEvent
    gameFinished: GameFinishedEvent
    enterAutoPool: EnterAutoPoolEvent
    gameStarted: GameStartedEvent
    reward: RewardEvent
    directChallenge: DirectChallengeEvent
    offerRematchResult: OfferRematchResultEvent
    emailActivated: EmailActivatedEvent
    loginPromptShown: LoginPromptShownEvent
    guestContinued: GuestContinuedEvent
    loginDismissed: LoginDismissedEvent
    confirmEmail: ConfirmEmailEvent
    requestResend: RequestResendEvent
    closeGuestWelcome: CloseGuestWelcomeEvent
    createGuestAccount: CreateGuestAccountEvent
    signupReachSecondPage: SignupReachSecondPageEvent
    nextPuzzle: NextPuzzleEvent
    missionCompleted: MissionCompletedEvent
    missionClaimed: MissionClaimedEvent
    // GTM-specific separate events
    offerAbort: OfferAbortEvent
    offerDraw: OfferDrawEvent
    acceptRematch: AcceptRematchEvent
    computerChallenge: ComputerChallengeEvent
    timeInQueue: TimeInQueueEvent
    // new events
    ftuePuzzleStarted: FtuePuzzleStartedEvent
    ftuePuzzleCompleted: FtuePuzzleCompletedEvent
    popupTriggered: PopupEvents
    popupShown: PopupShownEvent
    popupClosed: PopupClosedEvent
    ctaSelected: CTASelectedEvent
    dailyBonusClicked: DailyBonusClickedEvent
    dailyBonusCollected: DailyBonusCollectedEvent
    powerUpUsed: PowerUpUsedEvent
    ftueEndDialogue: FtueEndDialogueEvent
    feedbackPopupShown: FeedbackPopupShownEvent
    feedbackPopupClosed: FeedbackPopupClosedEvent
    menuButtonClicked: MenuButtonClickedEvent
    startedPuzzle: StartedPuzzleEvent
    gameButtonPressed: GameButtonPressedEvent
    flipBoardPressed: FlipBoardPressedEvent
    shareClicked: ShareClickedEvent
    shareLinkCopied: ShareLinkCopiedEvent
    feedbackShared: FeedbackSharedEvent
    pvpEmoteSent: PvpEmoteSentEvent
    pvpEmoteMenuOpened: PvpEmoteMenuOpenedEvent
}

export type EventListenerFunction<K extends keyof AnalyticsEventMap> = (ev: AnalyticsEventMap[K]) => any

export type EventListeners = {
    [key in keyof AnalyticsEventMap]?: EventListenerFunction<key>[]
}

export interface AnalyticsManagerInterface {
    markAsLoaded(): void

    addEventListener<K extends keyof AnalyticsEventMap>(type: K, listener: EventListenerFunction<K>): void

    removeEventListener<K extends keyof AnalyticsEventMap>(type: K, listener: EventListenerFunction<K>): void

    dispatchEvent<K extends keyof AnalyticsEventMap>(type: K, ev: AnalyticsEventMap[K]): void
}

export class AnalyticsManager implements AnalyticsManagerInterface {
    private eventListeners: EventListeners
    private loading: Promise<void>
    private resolveLoading!: () => void

    constructor() {
        this.eventListeners = {}
        this.loading = new Promise((resolve) => {
            this.resolveLoading = resolve
        })
    }

    markAsLoaded(): void {
        this.resolveLoading()
    }

    addEventListener<K extends keyof AnalyticsEventMap>(type: K, listener: EventListenerFunction<K>): void {
        if (!this.eventListeners[type]) {
            this.eventListeners[type] = []
        }

        this.eventListeners[type]!.push(listener)
    }

    removeEventListener<K extends keyof AnalyticsEventMap>(type: K, listener: EventListenerFunction<K>): void {
        const listeners = this.eventListeners[type]
        if (!listeners) {
            return
        }

        this.eventListeners[type] = listeners.filter((l) => l !== listener) as typeof listeners
    }

    async dispatchEvent<K extends keyof AnalyticsEventMap>(type: K, ev: AnalyticsEventMap[K]): Promise<void> {
        await this.loading
        this.eventListeners[type]?.forEach((listener) => {
            try {
                listener(ev)
            } catch (e) {
                console.error(`Error occurred in listener for event type '${type}':`, e)
            }
        })
    }
}

export const analyticsManager = new AnalyticsManager()
